import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PhotoThumbs } from "@/components/fleet/Photos";

const CloseFault = ({ fault, onClose, onSaved }) => {
  const [resolution, setResolution] = useState("");
  const save = async () => {
    try {
      const { data } = await api.post(`/faults/${fault.id}/close`, { resolution });
      toast.success("Fault closed");
      onSaved(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="close-fault-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Close fault</DialogTitle></DialogHeader>
        <p className="text-sm"><b>{fault.bike_name}</b> · {fault.item_label}: {fault.note}</p>
        <Textarea rows={3} placeholder="What was done to fix it?" value={resolution} onChange={(e) => setResolution(e.target.value)} data-testid="fault-resolution-input" />
        <div className="flex justify-end"><button onClick={save} data-testid="fault-close-confirm" className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Close fault</button></div>
      </DialogContent>
    </Dialog>
  );
};

export const FaultsList = ({ canManage }) => {
  const [status, setStatus] = useState("open");
  const [faults, setFaults] = useState(null);
  const [closing, setClosing] = useState(null);

  useEffect(() => {
    api.get("/faults", { params: { status: status || undefined } }).then((r) => setFaults(r.data));
  }, [status]);

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        {[["open", "Open"], ["closed", "Closed"], ["", "All"]].map(([k, l]) => (
          <button key={l} onClick={() => setStatus(k)} data-testid={`fault-filter-${l.toLowerCase()}`} className={`rounded-full border px-4 py-1.5 text-sm font-medium ${status === k ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>
      <div className="overflow-hidden rounded-2xl border bg-card" data-testid="faults-list">
        {faults?.length === 0 && <p className="px-5 py-10 text-sm text-muted-foreground">No faults.</p>}
        {faults?.map((f) => (
          <div key={f.id} data-testid={`fault-row-${f.id}`} className={`space-y-2 border-b px-5 py-4 last:border-0 ${f.status === "open" ? "border-l-4 border-l-amber-500" : ""}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="font-semibold">{f.bike_name} · {f.item_label}</div>
                <div className="text-sm text-muted-foreground">{f.note}</div>
                <div className="text-xs text-muted-foreground">{f.stage === "pre" ? "Pre-ride" : "Post-ride"} · {f.reported_by} · {fmtDateTime(f.reported_at)} · {f.run_id ? <Link to={`/runs/${f.run_id}`} className="text-primary hover:underline">View run</Link> : <span data-testid={`fault-not-taken-${f.id}`} className="rounded bg-red-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-300">Bike not taken</span>}</div>
                {f.status === "closed" && <div className="mt-1 text-xs text-emerald-400">Closed by {f.closed_by}: {f.resolution}</div>}
              </div>
              {canManage && f.status === "open" && <button onClick={() => setClosing(f)} data-testid={`fault-close-${f.id}`} className="rounded-full border px-4 py-1.5 text-xs font-semibold hover:bg-secondary">Close fault</button>}
            </div>
            {f.photos?.length > 0 && <PhotoThumbs photos={f.photos} />}
          </div>
        ))}
      </div>
      {closing && <CloseFault fault={closing} onClose={() => setClosing(null)} onSaved={(d) => setFaults((x) => (status === "open" ? x.filter((y) => y.id !== d.id) : x.map((y) => (y.id === d.id ? d : y))))} />}
    </div>
  );
};
